import { useState, useMemo, useCallback, useEffect } from 'react';
import { GroupComment } from '@cobu/types';

interface UseCommentRepliesConfig {
  replies: GroupComment[];
  currentUserId: string;
  parentComment: GroupComment;
  onReply?: (comment: GroupComment) => void;
  onReact?: (commentId: string, reaction: string | null) => void;
  forceExpandReplies?: boolean;
}

export const useCommentReplies = ({
  replies,
  currentUserId,
  parentComment,
  onReply,
  onReact,
  forceExpandReplies = false,
}: UseCommentRepliesConfig) => {
  // Internal state for expand/collapse
  const [expanded, setExpanded] = useState(false);

  // Filter out removed replies (except user's own) - memoized for performance
  const visibleReplies = useMemo(() => 
    replies.filter(reply => 
      !reply.removed || reply.removedBy === 'USER' || reply.user.uuid === currentUserId
    ), [replies, currentUserId]
  );

  // Determine replies to show based on expanded state
  const repliesToShow = useMemo(() => {
    if (visibleReplies.length === 0) return [];
    const lastReply = visibleReplies[visibleReplies.length - 1];
    return expanded ? visibleReplies : [lastReply];
  }, [visibleReplies, expanded]);

  // Check if there are multiple replies
  const hasMultipleReplies = useMemo(() => visibleReplies.length > 1, [visibleReplies]);

  // Force expand replies for deep-linked reply targets
  useEffect(() => {
    if (forceExpandReplies && !expanded) {
      setExpanded(true);
    }
  }, [forceExpandReplies, expanded]);

  // Handle expand/collapse - memoized to prevent unnecessary re-renders
  const handleToggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  // Handle reply action - memoized callback
  const handleReply = useCallback((reply: GroupComment) => {
    onReply?.(parentComment); // Always reply to parent, not to the reply itself
  }, [onReply, parentComment]);

  // Handle reaction action - memoized callback
  const handleReact = useCallback((commentId: string, reaction: string | null) => {
    onReact?.(commentId, reaction);
  }, [onReact]);

  return {
    expanded,
    visibleReplies,
    repliesToShow,
    hasMultipleReplies,
    handleToggleExpanded,
    handleReply,
    handleReact,
  };
}; 